<template>
  <div v-if="isInitialized" class="summary resources__page-topic">
    <div v-if="activeEntry" class="resources__top-wrap">
      <event-details :activeEntry="activeEntry" />
    </div>
    <div v-else>
      <div class="resources__top-wrap">
        <RichRenderer :richTextData="cPage?.fields?.paragraphRich" />
      </div>
      <div class="summary__items-wrapper">
        <template v-if="notExpiredEvents.length">
          <event-item
            v-for="eventItem in notExpiredEvents"
            :key="eventItem.title"
            :eventItem="eventItem"
            @clicked="entryClicked(eventItem)"
          />
        </template>
        <h2 v-else class="text-h3 text-color-navy summary__no-results">
          {{ $t('resources.noUpcomingEventsFound') }}
        </h2>
      </div>
      <div class="summary__archive">
        <app-button
          v-if="!isArchiveVisible"
          :text="pastEventsButtonText"
          size="sub-middle"
          variant="secondary-inversed"
          type="button"
          class="interior-hero__header-btn"
          @clicked="isArchiveVisible = true"
        />
        <events-archive v-else :expiredEvents="expiredEvents" @eventClicked="entryClicked" />
      </div>
    </div>
  </div>
</template>
<script>
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { isEventExpired, convertDateToItsZone } from '@/lib/utils/dates'
import sorting from '@/lib/utils/sorting'

import { useContentfulComponent } from '@/components/common/contentful/composables/useContentfulComponent'

import EventDetails from '@/components/resources/pages/EventDetails.vue'
import EventItem from '@/components/resources/particles/EventItem.vue'
import EventsArchive from '@/components/resources/particles/EventsArchive.vue'

import { useErrorService } from '@/composables/useErrorService'
import { useI18n } from '@/composables/useI18n'
import { usePageMeta } from '@/composables/usePageMeta'
import { useTransformEntries } from '@/composables/useTransformEntries'

export default {
  name: 'EventSummary',
  components: {
    EventDetails,
    EventItem,
    EventsArchive,
  },
  props: {
    cPage: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { fetchItems, getSiteCopy } = useContentfulComponent()
    const { setPageMeta } = usePageMeta()
    const errorService = useErrorService()
    const { transformEntry } = useTransformEntries()
    const router = useRouter()
    const { t } = useI18n()

    const activeEntry = ref(null)
    const allEntries = ref([])
    const expiredEvents = ref([])
    const notExpiredEvents = ref([])
    const isArchiveVisible = ref(false)
    const pastEventsButtonText = ref('')
    const isInitialized = ref(false)

    const entryClicked = (entry) => {
      router.push({
        path: router.currentRoute.value.path,
        query: { entry: entry.title },
      })
    }
    const getMonth = (date) => {
      return convertDateToItsZone(date, 'MMM')
    }
    const getDay = (date) => {
      return convertDateToItsZone(date, 'DD')
    }
    const getFullDate = (date) => {
      return convertDateToItsZone(date, 'LL')
    }
    const setEntry = () => {
      activeEntry.value = allEntries.value.find(
        (el) => el.title === decodeURIComponent(router.currentRoute.value.query.entry),
      )
    }

    const initData = (entries) => {
      const transformedEvents = entries.map((entry) => {
        const transformedEntry = transformEntry(entry)
        return {
          ...transformedEntry,
          isExpired: false,
          displayMonth: getMonth(transformedEntry.date),
          displayDay: getDay(transformedEntry.date),
          displayFullDate: getFullDate(transformedEntry.date),
          displayAdditionalDateFullDate: getFullDate(transformedEntry.additionalDate),
        }
      })
      const allEntriesTemp = sorting.sortObjectsListByDateAsc(transformedEvents, 'date')
      const notExpiredEventsTemp = []
      const expiredEventsTemp = []
      allEntriesTemp.forEach((event) => {
        const isExpired = isEventExpired(event.date, event.additionalDate)
        event.isExpired = isExpired
        const listToPush = isExpired ? expiredEventsTemp : notExpiredEventsTemp
        listToPush.push(event)
      })
      allEntries.value = allEntriesTemp
      expiredEvents.value = sorting.sortObjectsListByDateDesc(expiredEventsTemp, 'date')
      notExpiredEvents.value = notExpiredEventsTemp

      pastEventsButtonText.value =
        getSiteCopy('copyGroupCommon', 'copyItemEventsPagePastEventsButton') ||
        t('resources.pastEvents')

      setEntry()
      isInitialized.value = true
    }

    setPageMeta(props.cPage?.fields?.pageMeta)

    onMounted(async () => {
      const query = {
        skip: 0,
        limit: 1000,
        include: 2,
        order: '-fields.date',
        content_type: 'resource',
        'fields.resourceGroup.sys.id': props?.cPage?.sys?.id,
      }
      const entries = await fetchItems(query)
      if (!entries) {
        const error = new Error('No contentful entry on Resource request')
        errorService.decorateError('CONTENTFUL', error, { errorLevel: 'network' })
        throw error
      } else {
        initData(entries)
      }
    })

    watch(
      () => router.currentRoute.value,
      () => {
        setEntry()
      },
    )

    return {
      t,
      isInitialized,
      activeEntry,
      expiredEvents,
      notExpiredEvents,
      isArchiveVisible,
      pastEventsButtonText,
      entryClicked,
    }
  },
}
</script>
